import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/buttons/Button";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import RadioGroup from "~/components/formElements/RadioGroup";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { INVOICE_BY_MAIL_OPTIONS } from "~/constants/form.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	createInvoiceRecipient,
	updateInvoiceRecipient,
} from "~/modules/billing/api/invoiceRecipient/invoiceRecipientApiDispatchers.ts";
import type {
	CreateOrUpdateInvoiceRecipientData,
	InvoiceRecipient,
} from "~/modules/billing/api/invoiceRecipient/invoiceRecipientTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type CreateOrUpdateInvoiceRecipientFormProps = {
	clientId: string,
	initialValues?: InvoiceRecipient
	onCancelClick: () => void;
	onSuccess: () => void;
};

type CreateOrUpdateInvoiceRecipientFormData = Omit<CreateOrUpdateInvoiceRecipientData, "clientId">
const CreateOrUpdateInvoiceRecipientForm: React.FunctionComponent<CreateOrUpdateInvoiceRecipientFormProps> = ({
	clientId,
	initialValues,
	onCancelClick,
	onSuccess,
}) => {
	const [busy, setBusy] = useState(false);
	const isUpdateProcess = !!initialValues;
	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().default(""),
			displayName: yup.string().required().default(""),
			invoiceEmail: yup.string().email().when("invoiceByMail", {
				is: true,
				then: (schema) => schema.required(),
				otherwise: (schema) => schema.optional(),
			}).default(""),
			invoiceByMail: yup.boolean().required(),
			invoiceHead: yup.string().required().default(""),
			supportContactName: yup.string().default(""),
			supportContactEmail: yup.string().default(""),
			supportContactPhone: yup.string().default(""),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			comment: initialValues?.comment || "",
			displayName: initialValues?.displayName || "",
			invoiceEmail: initialValues?.invoiceEmail || "",
			invoiceByMail: initialValues?.invoiceByMail === undefined ? true : initialValues.invoiceByMail,
			invoiceHead: initialValues?.invoiceHead || "",
			supportContactName: initialValues?.supportContactName || "",
			supportContactEmail: initialValues?.supportContactEmail || "",
			supportContactPhone: initialValues?.supportContactPhone || "",
		};
	}, [initialValues]);

	const {
		handleSubmit,
		formState: { isValid, isDirty, isSubmitted },
		control,
		trigger,
		watch
	} = useForm<CreateOrUpdateInvoiceRecipientFormData>({
		defaultValues: defaultValues,
		mode: isUpdateProcess ? "onChange" : "onSubmit",
		resolver: yupResolver<CreateOrUpdateInvoiceRecipientFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isSubmitted,
		isValid,
		isLoading: busy,
		enableInitially: !isUpdateProcess,
	});

	useEffect(() => {
		if(isUpdateProcess){
			trigger();
		}
	}, [isUpdateProcess, trigger]);

	const onSubmit = async (data: CreateOrUpdateInvoiceRecipientFormData) => {
		setBusy(true);

		try {
			if (isUpdateProcess) {
				await updateInvoiceRecipient({ clientId, invoiceRecipientId: initialValues.id, data });
			} else {
				await createInvoiceRecipient({ clientId, data });
			}
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	const invoiceByMail = watch('invoiceByMail');

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>{initialValues ? "Rechnungsempfänger aktualisieren" : "Rechnungsempfänger erstellen"}</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Name / Titel">
						<Input
							name="displayName"
							control={control}
						/>
					</FormSection>
					<FormSection title="Rechnungsanschrift / Rechnungskopf">
						<Textarea
							rows={5}
							control={control}
							name="invoiceHead"
							placeholder="Vollständige Adresse für Rechnungsdokument und / oder Versand"
						/>
					</FormSection>
					<FormSection title="Rechnungsversand">
						<div className="space-y-4">
							<RadioGroup
								control={control}
								label="Versand per"
								name="invoiceByMail"
								options={INVOICE_BY_MAIL_OPTIONS}
								numCols={2}
							/>
							{invoiceByMail && <Input
								name="invoiceEmail"
								control={control}
								label="E-Mail"
							/>}
						</div>
					</FormSection>
					<FormSection title="Support Kontakt">
						<div className="grid grid-cols-6 gap-6">
							<div className="col-span-6">
								<Input
									name="supportContactName"
									control={control}
									label="Ansprechpartner:in"
								/>
							</div>
							<div className="col-span-3">
								<Input
									name="supportContactPhone"
									control={control}
									label="Telefon"
								/>
							</div>
							<div className="col-span-3">
								<Input
									name="supportContactEmail"
									control={control}
									label="E-Mail"
								/>
							</div>
						</div>
					</FormSection>

					<FormSection title="Anmerkungen">
						<Textarea
							control={control}
							name="comment"
						/>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isUpdateProcess && !isValid || (!isUpdateProcess && !isValid && isSubmitted)}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancelClick}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateOrUpdateInvoiceRecipientForm;